import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Box, Container, Fade, Grid, Stack, Typography } from '@mui/material';

import CampaignListItem from '../components/CampaignListItem';
import { GoogleAuthProfilePicButton } from '../components/minicomponents/GoogleAuthProfilePicButton';
import { GradientButton } from '../components/minicomponents/GradientButton';
import { IconAddCircle } from '../components/minicomponents/icons';
import AlertPopup from '../components/popups/AlertPopup';
import LoadingPopup from '../components/popups/LoadingPopup';
import NewCampaignPopup from '../components/popups/NewCampaignPopup';
import UpgradePopup from '../components/popups/UpgradePopup';
import { trackClickButtonNewCampaign } from '../utils/analytics/analytics';
import { trackSentryError } from '../utils/analytics/sentry';
import { createNewCampaign, handleApiError as handleApiErrorUtil } from '../utils/api';
import { ANIMATION_DURATION_MS } from '../utils/const';
import { useListenUserCampaigns } from '../utils/db';
import { useFirebaseLoginState } from '../utils/hooks';
import { resetInputDataSection, setManualDescription, setUrl } from '../utils/state';
import { RootState } from '../utils/store';

const EmptyCampaignsList = ({
  onCreateNewCampaignButtonHandler,
}: {
  onCreateNewCampaignButtonHandler: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Grid
      container
      size={12}
      textAlign="center"
      justifyContent="center"
      alignItems="center"
    >
      <Grid size={12}>
        <Typography variant="h1" sx={{ mt: 5, mb: 5, color: 'text.secondary' }}>
          {t('app.screens.campaigns.noCampaigns')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
          {t('app.screens.campaigns.createCampaignHint')}
        </Typography>
        <GradientButton
          version="contained"
          buttonText={t('app.buttons.createCampaign')}
          iconLeft={<IconAddCircle />}
          onSubmitButtonHandler={onCreateNewCampaignButtonHandler}
        />
      </Grid>
    </Grid>
  );
};

const SignInInvitation = () => {
  const { t } = useTranslation();
  return (
    <Grid container size={12} justifyContent="center">
      <Stack spacing={2} sx={{ mt: 5 }} alignItems="center">
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ maxWidth: '600px', mb: 1 }}
        >
          {t('website.googleAuthButton.signInInvitation')}
        </Typography>
        <Box>
          <GoogleAuthProfilePicButton />
        </Box>
      </Stack>
    </Grid>
  );
};

export const CampaignsListScreen: React.FC = () => {
  const [isNewCampaignPopupOpen, setNewCampaignPopupOpen] = React.useState(false);
  const [isLoadingProgressOpen, setLoadingProgressOpen] = React.useState(false);
  const [pendingCampaignParams, setPendingCampaignParams] = useState<{
    url: string;
    prompt: string;
    mode: string;
  } | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const state = useSelector((state: RootState) => state.appState);
  const [isUpgradePopupOpen, setUpgradePopupOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { userId, isRegistered, isAuthStateLoading } = useFirebaseLoginState();
  const { campaigns, loading, error: campaignsError } = useListenUserCampaigns(userId);

  useEffect(() => {
    if (pendingCampaignParams || !location.search) return;

    const searchParams = new URLSearchParams(location.search);
    const isCreateNewCampaign = searchParams.get('is_create_new_campaign') === 'true';
    const urlParam = searchParams.get('url');
    const manualDescriptionParam = searchParams.get('manual_description');

    if (isCreateNewCampaign && (urlParam || manualDescriptionParam)) {
      setPendingCampaignParams({
        url: urlParam || '',
        prompt: manualDescriptionParam || '',
        mode: 'url',
      });

      dispatch(setUrl(urlParam || ''));
      dispatch(setManualDescription(manualDescriptionParam || ''));
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, pendingCampaignParams, dispatch, navigate]);

  useEffect(() => {
    if (
      pendingCampaignParams &&
      userId &&
      state.creativity &&
      state.outputLanguage &&
      state.session
    ) {
      const params = pendingCampaignParams;
      setPendingCampaignParams(null);
      handleCreateCampaign(params);
    }
  }, [
    pendingCampaignParams,
    userId,
    state.creativity,
    state.outputLanguage,
    state.session,
  ]);

  const handleNewCampaignOpenPopup = () => {
    setNewCampaignPopupOpen(true);
  };

  const handleNewCampaignClosePopup = () => {
    setNewCampaignPopupOpen(false);
  };

  const onCreateNewCampaignButtonHandler = () => {
    trackClickButtonNewCampaign('campaigns');
    handleNewCampaignOpenPopup();
  };

  const handleError = useCallback((error: unknown) => {
    handleApiErrorUtil({
      error,
      onSetUpgradePopupOpen: setUpgradePopupOpen,
      onSetErrorMessage: setError,
      onSetErrorAlertOpen: (isOpen) => {
        setIsAlertOpen(isOpen);
        if (!isOpen) setError(null);
      },
    });
  }, []);

  const handleAlertSubmit = () => {
    setIsAlertOpen(false);
    handleNewCampaignOpenPopup();
  };

  const handleCreateCampaign = React.useCallback(
    async (params: { url: string; prompt: string; mode: string }) => {
      setNewCampaignPopupOpen(false);
      setLoadingProgressOpen(true);

      try {
        if (!userId) {
          throw new Error('User is not signed in');
        }

        const newCampaignId = await createNewCampaign(
          userId,
          params.url,
          params.prompt,
          state.creativity,
          state.outputLanguage,
          state.session
        );

        dispatch(resetInputDataSection());
        navigate(`/app/campaigns/${newCampaignId}${location.search}`);
      } catch (error) {
        trackSentryError('Error creating campaign in CampaignsListScreen', { error });
        console.error('Error creating campaign:', error);
        if (error instanceof Error && error.message.toLowerCase().includes('url')) {
          setLoadingProgressOpen(false);
          setNewCampaignPopupOpen(true);
        }
        handleError(error);
      } finally {
        setLoadingProgressOpen(false);
      }
    },
    [
      userId,
      state.creativity,
      state.outputLanguage,
      state.session,
      dispatch,
      navigate,
      handleError,
    ]
  );

  return (
    <Container maxWidth="desktop" sx={{ pt: 4 }}>
      <Grid container gap={3}>
        <Grid size={12} container justifyContent="space-between" alignItems="center">
          <Grid>
            <Fade in={true} timeout={ANIMATION_DURATION_MS}>
              <Typography variant="h1" fontSize={24}>
                {t('app.screens.campaigns.title')}
              </Typography>
            </Fade>
          </Grid>
          {userId !== null && (userId === '' || campaigns.length > 0) && (
            <Grid>
              <GradientButton
                version="contained"
                buttonText={t('app.buttons.newCampaign')}
                iconLeft={<IconAddCircle />}
                onSubmitButtonHandler={onCreateNewCampaignButtonHandler}
              />
            </Grid>
          )}
        </Grid>

        {loading && userId !== null ? (
          <> </>
        ) : campaignsError ? (
          <Typography color="error">
            {t('app.errors.loadingCampaigns', { message: campaignsError.message })}
          </Typography>
        ) : campaigns.length > 0 ? (
          <Grid container gap={2} size={12}>
            {campaigns.map((campaign) => (
              <CampaignListItem key={campaign.id} campaign={campaign} />
            ))}
          </Grid>
        ) : (
          <EmptyCampaignsList
            onCreateNewCampaignButtonHandler={onCreateNewCampaignButtonHandler}
          />
        )}

        {/* New Sign-in Prompt Section */}
        {!isRegistered && !loading && !isAuthStateLoading && <SignInInvitation />}
      </Grid>
      <NewCampaignPopup
        open={isNewCampaignPopupOpen}
        onClose={handleNewCampaignClosePopup}
        onSubmit={handleCreateCampaign}
        location="campaigns"
      />
      <LoadingPopup open={isLoadingProgressOpen} />
      <AlertPopup
        open={isAlertOpen}
        onClose={() => setIsAlertOpen(false)}
        onSubmit={handleAlertSubmit}
        alertText={error || ''}
        title={t('app.popups.error.title')}
        buttonText={t('app.buttons.ok')}
      />
      <UpgradePopup
        open={isUpgradePopupOpen}
        onClose={() => setUpgradePopupOpen(false)}
        onError={handleError}
      />
    </Container>
  );
};
